import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const resp = await axios.get(`${getApiUrl()}/auth/me`);
          setUser(resp.data.data.user);
        } catch (err) {
          console.error('Failed to restore mission session:', err);
          logout();
        }
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }

      // Add interceptor for 401 errors
      const interceptor = axios.interceptors.response.use(
        (response) => response,
        (error) => {
          const isAuthEndpoint = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register');
          
          if (error.response && error.response.status === 401 && !isAuthEndpoint) {
            logout();
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      );

      setLoading(false);
      return () => {
        axios.interceptors.response.eject(interceptor);
      };
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/login`, { email, password });
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (email, password, name, brand, market, country) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/register`, { email, password, name, brand, market, country });
      const { user, token } = response.data.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshProfile = async () => {
    if (token) {
      try {
        const resp = await axios.get(`${getApiUrl()}/auth/me`);
        setUser(resp.data.data.user);
        return resp.data.data.user;
      } catch (err) {
        console.error('Failed to refresh mission profile:', err);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
